import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, reactive, watch } from 'vue'

import HelpTooltip from '@/client/components/_atoms/HelpTooltip.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { validateKeywords } from '@/client/utils/validation/keyword'

import { useInstagramInstantwinPrizeStore } from '../hooks/instagramInstantwinPrizeStore'

type SaveMode = 'create' | 'edit'

interface Emits {
  (e: 'close'): void
  (e: 'saved', mode: SaveMode): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'InstagramInstantwinPrizeEditDialog',
  props: {
  visible: {
    type: Boolean,
    required: true
  },
  prizeId: {
    type: Number,
    default: null
  },
  campaignId: {
    type: Number,
    required: true
  },
  prizeCount: {
    type: Number,
    required: true
  },
  campaignStatus: {
    type: Number,
    default: null
  }
},
  emits: ["close", "saved"],
  setup(__props, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'saved', mode: SaveMode): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const { proxy } = getCurrentInstance() as any




// Store
const prizeStore = useInstagramInstantwinPrizeStore()
const { prize, loading } = storeToRefs(prizeStore)

// State
const formData = reactive({
  name: '',
  entryKeyword: [] as string[],
  maxWinnerCount: 0,
  dailyWinnerChangeTime: '00:00',
  dailyWinnerCount: 0,
  winningRateChangeType: 'none' as 'none' | 'auto',
  winningRate: 0
})

const validationErrors = reactive({
  name: '',
  winningRate: ''
})

// Computed
const isEditMode = computed(() => props.prizeId !== null && props.prizeId !== undefined)
const normalizedEntryKeywords = computed(() => formData.entryKeyword.map(keyword => keyword.trim()).filter(Boolean))
const isCampaignFinished = computed(() =>
  [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
    props.campaignStatus as number
  )
)
const isDeleteDisabled = computed(() => loading.value || props.prizeCount <= 1 || isCampaignFinished.value)

const isFormValid = computed(() => {
  // 必須フィールドチェック
  if (!formData.name || formData.name.trim() === '') {
    return false
  }

  // 数値範囲チェック
  if (formData.maxWinnerCount < 0) {
    return false
  }

  if (formData.winningRateChangeType === 'none') {
    if (formData.winningRate < 0 || formData.winningRate > 100) {
      return false
    }
  }

  if (formData.dailyWinnerCount < 0) {
    return false
  }

  // 1日の最大当選数は当選数を超えられない
  if (formData.dailyWinnerCount > 0 && formData.dailyWinnerCount > formData.maxWinnerCount) {
    return false
  }

  return true
})

// Methods
const validateForm = () => {
  let isValid = true

  // 応募グループ名の検証
  if (!formData.name || formData.name.trim() === '') {
    isValid = false
  }

  // 当選数の検証
  if (formData.maxWinnerCount < 0) {
    isValid = false
  }

  // 当選率の検証（変動なしの場合のみ）
  if (formData.winningRateChangeType === 'none') {
    if (formData.winningRate < 0 || formData.winningRate > 100) {
      isValid = false
    }
  }

  // 1日の最大当選数の検証
  if (formData.dailyWinnerCount < 0) {
    isValid = false
  }

  if (formData.dailyWinnerCount > 0 && formData.dailyWinnerCount > formData.maxWinnerCount) {
    isValid = false
  }

  return isValid
}

const resetFormData = () => {
  formData.name = ''
  formData.entryKeyword = []
  formData.maxWinnerCount = 0
  formData.dailyWinnerChangeTime = '00:00'
  formData.dailyWinnerCount = 0
  formData.winningRateChangeType = 'none'
  formData.winningRate = 0
}

const applyPrizeData = (prizeData: any) => {
  formData.name = prizeData.name
  try {
    formData.entryKeyword = prizeData.entryKeyword ? JSON.parse(prizeData.entryKeyword) : []
  } catch {
    formData.entryKeyword = []
  }
  formData.maxWinnerCount = prizeData.maxWinnerCount
  formData.dailyWinnerChangeTime = prizeData.dailyWinnerChangeTime
    ? prizeData.dailyWinnerChangeTime.substring(0, 5)
    : '00:00'
  formData.dailyWinnerCount = prizeData.dailyWinnerCount
  formData.winningRateChangeType = prizeData.winningRateChangeType as 'none' | 'auto'
  formData.winningRate = prizeData.winningRate
}

const notifyKeywordValidationError = (errorMessage?: string) => {
  proxy.$notify({
    title: errorMessage || 'キーワードに利用できない文字列が含まれています。',
    customClass: 'danger',
    duration: 5000
  })
}

const validateEntryKeywords = (keywords: string[], origin: string[] = []) => {
  return validateKeywords(keywords, origin, { mode: 'whitespace_and_length_only' })
}

const onChangeEntryKeywords = (keywords: string[]) => {
  const origin = [...formData.entryKeyword]
  const result = validateEntryKeywords(keywords, origin)

  if (!result.isValid) {
    notifyKeywordValidationError(result.errorMessage)
    return
  }

  formData.entryKeyword = keywords
}

const fetchPrizeData = async () => {
  if (!props.prizeId) {
    return
  }

  try {
    await prizeStore.fetchPrize(props.prizeId)
  } catch {
    proxy.$notify({
      title: 'データの読み込みに失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const getErrorType = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.type || null
  }

  const errorData = error as any
  return errorData?.response?.data?.error?.type || errorData?.error?.type || null
}

const getFailedMessage = (errorType: string | null) => {
  if (!errorType) {
    return isEditMode.value
      ? 'データの保存に失敗しました。しばらくしてから再度お試しください。'
      : '応募グループの新規作成に失敗しました。しばらくしてから再度お試しください。'
  }

  switch (errorType) {
    case 'PARAMETER_INVALID':
      return '入力内容に誤りがあります。'
    case 'NOT_EXISTS':
      return isEditMode.value ? '応募グループが見つかりませんでした。' : 'キャンペーンが見つかりませんでした。'
    default:
      return isEditMode.value
        ? 'データの保存に失敗しました。しばらくしてから再度お試しください。'
        : '応募グループの新規作成に失敗しました。しばらくしてから再度お試しください。'
  }
}

const getDeleteFailedMessage = (errorType: string | null) => {
  switch (errorType) {
    case 'NOT_EXISTS':
      return '対象の応募グループはすでに削除されています。'
    case 'PARAMETER_INVALID':
      return '応募グループを削除できませんでした。'
    default:
      return '応募グループの削除に失敗しました。しばらくしてから再度お試しください。'
  }
}

const handleSave = async () => {
  // バリデーションエラーをクリア
  validationErrors.name = ''

  // バリデーション
  if (!validateForm()) {
    // バリデーションエラーを表示
    if (!formData.name || formData.name.trim() === '') {
      validationErrors.name = '応募グループ名を入力してください'
    }
    return
  }

  const entryKeywordValidationResult = validateEntryKeywords(formData.entryKeyword)
  if (!entryKeywordValidationResult.isValid) {
    notifyKeywordValidationError(entryKeywordValidationResult.errorMessage)
    return
  }

  try {
    loading.value = true

    // API送信データの準備
    const saveData = {
      name: formData.name,
      entryKeyword: normalizedEntryKeywords.value,
      maxWinnerCount: formData.maxWinnerCount,
      // HH:mm形式をHH:mm:ss形式に変換
      dailyWinnerChangeTime: formData.dailyWinnerChangeTime ? `${formData.dailyWinnerChangeTime}:00` : '00:00:00',
      dailyWinnerCount: formData.dailyWinnerCount,
      winningRateChangeType: formData.winningRateChangeType,
      winningRate: formData.winningRate
    }

    const mode: SaveMode = isEditMode.value ? 'edit' : 'create'

    if (mode === 'edit') {
      await axios.put(`/api/instagram_instantwin_prizes/${props.prizeId}`, saveData)
      TrackingService.sendEvent('save:応募グループ編集（IGIW）')
      proxy.$notify({
        title: '応募グループを保存しました。',
        customClass: 'success',
        duration: 5000
      })
    } else {
      await prizeStore.createPrize({
        campaignId: props.campaignId,
        ...saveData
      })
      TrackingService.sendEvent('save:応募グループ新規作成（IGIW）')
      proxy.$notify({
        title: '応募グループを作成しました。',
        customClass: 'success',
        duration: 5000
      })
    }

    // 保存成功イベントを発火
    emit('saved', mode)
    emit('close')
  } catch (error) {
    proxy.$notify({
      title: getFailedMessage(getErrorType(error)),
      customClass: 'danger',
      duration: 5000
    })

    // トラッキング
    TrackingService.sendEvent(
      isEditMode.value ? 'error:応募グループ編集（IGIW）|保存失敗' : 'error:応募グループ新規作成（IGIW）|作成失敗'
    )
  } finally {
    loading.value = false
  }
}

const handleDelete = async () => {
  if (!props.prizeId || loading.value) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|応募グループ編集|削除')

  const confirmDelete = confirm('応募グループを削除してよろしいですか？')
  if (!confirmDelete) {
    return
  }

  try {
    loading.value = true
    await axios.delete(`/api/instagram_instantwin_prizes/${props.prizeId}`)
    proxy.$notify({
      title: '応募グループを削除しました。',
      customClass: 'success',
      duration: 5000
    })
    emit('saved', 'edit')
    emit('close')
  } catch (error) {
    const errorType = getErrorType(error)
    proxy.$notify({
      title: getDeleteFailedMessage(errorType),
      customClass: 'danger',
      duration: 5000
    })
    TrackingService.sendEvent('error:応募グループ編集（IGIW）|削除失敗')
    if (errorType === 'NOT_EXISTS') {
      emit('saved', 'edit')
      emit('close')
    }
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  emit('close')
}

const onChangeDailyWinnerCount = (dailyWinnerCount: number) => {
  if (!dailyWinnerCount) {
    setTimeout(() => {
      formData.dailyWinnerCount = 0
    }, 100)

    return
  }

  if (dailyWinnerCount > formData.maxWinnerCount) {
    setTimeout(() => {
      formData.dailyWinnerCount = 0
    }, 100)
  }
}

const onChangeWinningRate = (winningRate: number) => {
  if (!winningRate) {
    setTimeout(() => {
      formData.winningRate = 0
    }, 100)
    return
  }

  // 整数の場合は小数点を削除（100.00 → 100, 50.00 → 50）
  if (Number.isInteger(winningRate)) {
    setTimeout(() => {
      formData.winningRate = Math.floor(winningRate)
    }, 100)
  }
}

// Watch
watch(
  () => prize.value,
  prizeData => {
    if (props.visible && isEditMode.value && prizeData) {
      applyPrizeData(prizeData)
    }
  }
)

watch(
  () => props.visible,
  newValue => {
    if (newValue && isEditMode.value) {
      resetFormData()
      fetchPrizeData()
    } else if (newValue && !props.prizeId) {
      resetFormData()
    } else if (!newValue) {
      // ダイアログが閉じられたときにバリデーションエラーをクリア
      resetFormData()
      validationErrors.name = ''
    }
  }
)

watch(
  () => formData.name,
  newValue => {
    // フォームが空欄の場合はエラーを表示、入力がある場合はエラーをクリア
    if (!newValue || newValue.trim() === '') {
      validationErrors.name = '応募グループ名を入力してください'
    } else {
      validationErrors.name = ''
    }
  }
)

return { __sfc: true,proxy, props, emit, prizeStore, prize, loading, formData, validationErrors, isEditMode, normalizedEntryKeywords, isCampaignFinished, isDeleteDisabled, isFormValid, validateForm, resetFormData, applyPrizeData, notifyKeywordValidationError, validateEntryKeywords, onChangeEntryKeywords, fetchPrizeData, getErrorType, getFailedMessage, getDeleteFailedMessage, handleSave, handleDelete, handleClose, onChangeDailyWinnerCount, onChangeWinningRate, HelpTooltip, Panel, FormItemLabel, Tags }
}

})